import { BrochureResponseDto, brochureApi } from 'apiConnector/document/brochure';
import { BlobWithFilename, DocumentError } from 'apiConnector/document/document';
import { useStreamingContext } from 'contexts/streaming.context';
import { useCallback } from 'react';
import { EMPTY, first, firstValueFrom, map, mergeMap, Observable, throwError } from 'rxjs';
import { DocumentationEvent } from 'utils/streaming/model';

type GenerateDocumentArgs<GenerateReponse> = {
  isMessageRelevant: (ev: DocumentationEvent) => boolean;
  documentationMessage$: Observable<DocumentationEvent>;
  generateDocument: () => Observable<GenerateReponse>;
  isGenerateSuccessful: (r: GenerateReponse) => boolean;
  retrieveDoc: (r: GenerateReponse) => Observable<BlobWithFilename>;
  toDocumentError: (r: GenerateReponse) => DocumentError;
};

function waitAndDownload<GenerateReponse>({
  isGenerateSuccessful,
  isMessageRelevant,
  generateDocument,
  retrieveDoc,
  documentationMessage$,
  toDocumentError,
}: GenerateDocumentArgs<GenerateReponse>): Promise<BlobWithFilename> {
  return firstValueFrom(
    generateDocument().pipe(
      mergeMap(response => {
        if (isGenerateSuccessful(response)) {
          return documentationMessage$.pipe(
            first(isMessageRelevant),
            mergeMap(event => {
              switch (event.type) {
                case 'NextDocumentation':
                  return retrieveDoc(response);
                case 'NextDocumentationError':
                  return throwError(
                    () => ({ type: 'END_USER_ERROR', errorMessages: event.messages ?? [] }) satisfies DocumentError,
                  );
                default:
                  return throwError(() => ({ type: 'TECHNICAL' }) satisfies DocumentError) as never;
              }
            }),
          );
        } else {
          const documentError = toDocumentError(response);
          return throwError(() => documentError);
        }
      }),
    ),
  );
}

function mapBrochureResponse(response: { payload: Blob; attachmentName?: string }): BlobWithFilename {
  return {
    blob: response.payload,
    filename: response.attachmentName,
  };
}

export function useGenerateBrochureDocument(quoteId?: string): () => Promise<BlobWithFilename> {
  const { documentationMessage$ } = useStreamingContext();

  const startDocumentGeneration = useCallback(() => brochureApi.generateBrochure({ quoteId: quoteId! }), [quoteId]);

  const isMessageRelevant = useCallback(
    () => (m: DocumentationEvent) => m.quoteId === quoteId && (m.docType as string) === 'Brochure',
    [quoteId],
  );

  const retrieveDoc = useCallback(
    (response: BrochureResponseDto): Observable<BlobWithFilename> =>
      response.isSuccess ? brochureApi.downloadBrochure(response.id).pipe(map(mapBrochureResponse)) : EMPTY,
    [],
  );

  const generateDocument = useCallback(
    () =>
      waitAndDownload<BrochureResponseDto>({
        documentationMessage$,
        generateDocument: () => startDocumentGeneration(),
        isGenerateSuccessful: (response: BrochureResponseDto) => response.isSuccess,
        isMessageRelevant: isMessageRelevant(),
        retrieveDoc,
        toDocumentError: r =>
          r.isSuccess === false
            ? { type: 'END_USER_ERROR', errorMessages: r.errors }
            : ({ type: 'TECHNICAL' } satisfies DocumentError),
      }),
    [startDocumentGeneration, documentationMessage$, isMessageRelevant, retrieveDoc],
  );

  return generateDocument;
}
